import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Eye, Image, Video, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function Stories() {
  const { user } = useAuth();
  const [storyGroups, setStoryGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeStoryIdx, setActiveStoryIdx] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const fetchStories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/stories`, { credentials: "include" });
      if (res.ok) setStoryGroups(await res.json());
    } catch (e) { toast.error("Failed to load stories"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  useEffect(() => {
    if (activeGroup) {
      timerRef.current = setTimeout(() => {
        if (activeStoryIdx < activeGroup.stories.length - 1) {
          setActiveStoryIdx(activeStoryIdx + 1);
        } else {
          setActiveGroup(null);
          setActiveStoryIdx(0);
        }
      }, 5000);
      // Mark viewed
      const story = activeGroup.stories[activeStoryIdx];
      if (story) {
        fetch(`${API}/stories/${story.story_id}/view`, { method: "POST", credentials: "include" });
      }
      return () => clearTimeout(timerRef.current);
    }
  }, [activeGroup, activeStoryIdx]);

  const createStory = async (content, mediaUrl, mediaType) => {
    try {
      const res = await fetch(`${API}/stories`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, media_url: mediaUrl, media_type: mediaType }),
      });
      if (res.ok) { toast.success("Story posted!"); setShowCreate(false); fetchStories(); }
    } catch (e) { toast.error("Failed to create story"); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold glitter-text" data-testid="stories-title">Stories</h1>
          <button onClick={() => setShowCreate(true)} className="myspace-btn-primary flex items-center gap-2" data-testid="create-story-btn">
            <Plus className="w-4 h-4" /> New Story
          </button>
        </div>

        {/* Story Circles */}
        <div className="flex gap-4 overflow-x-auto pb-4 mb-6">
          {/* Add Story */}
          <div className="flex flex-col items-center gap-1 cursor-pointer flex-shrink-0" onClick={() => setShowCreate(true)} data-testid="add-story-circle">
            <div className="w-16 h-16 border-2 border-dashed border-[#FF00FF] flex items-center justify-center bg-[rgba(255,0,255,0.1)] hover:bg-[rgba(255,0,255,0.2)] transition-all">
              <Plus className="w-6 h-6 text-[#FF00FF]" />
            </div>
            <span className="text-xs text-[#FF66FF]">Add</span>
          </div>

          {storyGroups.map(group => (
            <div key={group.user_id} className="flex flex-col items-center gap-1 cursor-pointer flex-shrink-0"
              onClick={() => { setActiveGroup(group); setActiveStoryIdx(0); }}
              data-testid={`story-circle-${group.user_id}`}
            >
              <div className="w-16 h-16 border-2 border-[#FF00FF] overflow-hidden" style={{ boxShadow: "0 0 10px #FF00FF" }}>
                {group.author_picture ? (
                  <img src={group.author_picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#330066] flex items-center justify-center text-xl text-[#FF66FF]">
                    {group.author_name?.[0]}
                  </div>
                )}
              </div>
              <span className="text-xs text-[#99CCFF] max-w-[64px] truncate">{group.author_name}</span>
              <span className="text-[10px] text-[#6699CC]">{group.stories.length}</span>
            </div>
          ))}

          {!loading && storyGroups.length === 0 && (
            <p className="text-[#6699CC] text-sm self-center ml-4">No stories yet. Be the first to share!</p>
          )}
        </div>

        {/* Story feed as cards */}
        <div className="space-y-3">
          {storyGroups.map(group => group.stories.map(story => (
            <div key={story.story_id} className="myspace-card" data-testid={`story-card-${story.story_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-[#FF66FF] font-bold">{story.author_name}</span>
                <span className="text-xs text-[#6699CC]">{new Date(story.created_at).toLocaleString()}</span>
                <span className="text-xs text-[#6699CC] flex items-center gap-1 ml-auto">
                  <Eye className="w-3 h-3" /> {story.views?.length || 0}
                </span>
              </div>
              {story.content && <p className="text-[#CCCCFF] text-sm mb-2">{story.content}</p>}
              {story.media_url && story.media_type === "image" && (
                <img src={story.media_url} alt="" className="w-full max-h-64 object-contain border border-[#6600CC]" />
              )}
              {story.media_url && story.media_type === "video" && (
                <video src={story.media_url} controls muted className="w-full max-h-64 border border-[#6600CC]" />
              )}
            </div>
          )))}
        </div>
      </main>

      {/* Story Viewer */}
      <AnimatePresence>
        {activeGroup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={() => { setActiveGroup(null); setActiveStoryIdx(0); }}
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}
              data-testid="story-viewer"
            >
              {/* Progress bars */}
              <div className="flex gap-1 mb-2">
                {activeGroup.stories.map((s, i) => (
                  <div key={s.story_id || `progress-${i}`} className="flex-1 h-1 bg-[#333]">
                    <div className={`h-full transition-all duration-[5000ms] ${i < activeStoryIdx ? "bg-[#FF00FF] w-full" : i === activeStoryIdx ? "bg-[#FF00FF] animate-grow-width" : "w-0"}`} />
                  </div>
                ))}
              </div>

              <div className="border-2 border-[#FF00FF] bg-[#000033] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-[#FF66FF] font-bold">{activeGroup.author_name}</span>
                  <button onClick={() => { setActiveGroup(null); setActiveStoryIdx(0); }} className="text-[#FF66FF]">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {(() => {
                  const story = activeGroup.stories[activeStoryIdx];
                  if (!story) return null;
                  return (
                    <div className="min-h-[300px] flex flex-col items-center justify-center">
                      {story.media_url && story.media_type === "image" && (
                        <img src={story.media_url} alt="" className="max-w-full max-h-[400px] object-contain mb-3" />
                      )}
                      {story.media_url && story.media_type === "video" && (
                        <video src={story.media_url} autoPlay muted className="max-w-full max-h-[400px] mb-3" />
                      )}
                      {story.content && <p className="text-[#CCCCFF] text-center">{story.content}</p>}
                    </div>
                  );
                })()}
              </div>

              {/* Navigate */}
              <div className="flex justify-between mt-2">
                <button onClick={() => activeStoryIdx > 0 && setActiveStoryIdx(activeStoryIdx - 1)} className="myspace-btn text-xs px-3 py-1" disabled={activeStoryIdx === 0}>Prev</button>
                <button onClick={() => {
                  if (activeStoryIdx < activeGroup.stories.length - 1) setActiveStoryIdx(activeStoryIdx + 1);
                  else { setActiveGroup(null); setActiveStoryIdx(0); }
                }} className="myspace-btn text-xs px-3 py-1">
                  {activeStoryIdx < activeGroup.stories.length - 1 ? "Next" : "Close"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showCreate && <CreateStoryModal onClose={() => setShowCreate(false)} onCreate={createStory} />}
      </AnimatePresence>
    </div>
  );
}

function CreateStoryModal({ onClose, onCreate }) {
  const [content, setContent] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type.startsWith("image/")) setMediaType("image");
    else if (file.type.startsWith("video/")) setMediaType("video");
    else { toast.error("Please select an image or video"); return; }
    setMediaFile(file);
  };

  const handleCreate = async () => {
    setUploading(true);
    let mediaUrl = null;
    try {
      if (mediaFile) {
        const formData = new FormData();
        formData.append("file", mediaFile);
        const endpoint = mediaType === "video" ? `${API}/upload/video` : `${API}/upload/image`;
        const res = await fetch(endpoint, { method: "POST", credentials: "include", body: formData });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        mediaUrl = `${API}/files/${data.file_id}`;
      }
      onCreate(content, mediaUrl, mediaType);
    } catch (e) { toast.error(e.message); setUploading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="myspace-card w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="create-story-modal">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-bold text-[#00FFFF]">New Story</h2>
          <button onClick={onClose} className="myspace-btn px-2 py-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="What's on your mind?" className="myspace-input min-h-[80px] resize-none" data-testid="story-content-input" />
          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" data-testid="story-media-input" />
          {!mediaFile ? (
            <div className="flex gap-2">
              <button onClick={() => fileRef.current?.click()} className="myspace-btn flex-1 flex items-center justify-center gap-2">
                <Image className="w-4 h-4" /> Add Media
              </button>
            </div>
          ) : (
            <div className="p-2 border border-[#6600CC]">
              <p className="text-sm text-[#00FFFF] truncate">{mediaFile.name}</p>
              <button onClick={() => { setMediaFile(null); setMediaType(null); }} className="text-xs text-[#FF6666]">Remove</button>
            </div>
          )}
          <button onClick={handleCreate} disabled={(!content.trim() && !mediaFile) || uploading} className="myspace-btn-primary w-full disabled:opacity-50" data-testid="post-story-btn">
            {uploading ? "Posting..." : "Post Story"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
